import PropTypes from "prop-types";
import React, { useState } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { SMALL, useDeviceInfo, XLARGE } from "@swa-ui/browser";
import { Heading, Layout, ListScroller } from "@swa-ui/core";
import { useDestinationThemeModuleAnalytics } from "@swa-ui/dotcom-analytics";
import { useRecentSearches } from "@swa-ui/form";
import { stationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";
import { CallToAction, TextContent } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { DestinationByThemeCard } from "./DestinationByThemeCard";
import styles from "./DestinationByThemePlacement.module.scss";

export const DestinationByThemePlacement = (props) => {
  const { placement, placementData } = props;
  const { callToAction, graphic, link, maxCount } = placement ?? {};
  const { isLargeOrXlarge, screenSize } = useDeviceInfo();
  const { recentSearches } = useRecentSearches({ maxResults: 3 });
  const recentSearchDestinationAirports = getRecentSearches(
    recentSearches?.length > 0 ? recentSearches : null
  );
  const [imageHeight, setImageHeight] = useState(0);
  const SEARCH_TYPE_AIR = "air";
  const theme = getTheme(recentSearchDestinationAirports);

  useDestinationThemeModuleAnalytics(placementData, theme?.stations);

  return screenSize === SMALL ? renderContentSmall() : renderContent();

  function renderContentSmall() {
    return (
      <>
        <Layout bottom="medium" top="large">
          <Heading {...getHeadingProps()} />
        </Layout>
        <ListScroller {...getListScrollerProps()} />
        {renderCallToAction()}
      </>
    );
  }

  function renderContent() {
    return (
      <>
        <Layout {...getLayoutProps()}>
          <Heading {...getHeadingProps()} />
          {renderCallToAction()}
        </Layout>
        <ListScroller {...getListScrollerProps()} />
      </>
    );
  }

  function renderCallToAction() {
    return (
      callToAction && <CallToAction {...callToAction} paddingForFocus={screenSize !== SMALL} />
    );
  }

  function renderDestinationByThemeCard(item) {
    const { getShortDisplayStationName } = stationMethods;
    const stationDisplayName = getShortDisplayStationName(item);

    return (
      <DestinationByThemeCard
        key={item}
        onImageLoad={handleImageLoad}
        {...getDestinationByThemeCardProps(stationDisplayName, item)}
      />
    );
  }

  function getLayoutProps() {
    return {
      bottom: "medium",
      center: screenSize === XLARGE,
      className: styles.layoutContainer,
      top: "large",
    };
  }

  function getHeadingProps() {
    return {
      children: (
        <TextContent
          value={i18n(`DestinationByThemePlacement__TITLE_${theme?.category?.toUpperCase()}`)}
        />
      ),
      className: styles.heading,
      headingLevel: 2,
    };
  }

  function getListScrollerProps() {
    return {
      "aria-label-current-page": i18n("DestinationByThemePlacement__ARIA_LABEL_CURRENT_PAGE"),
      "aria-label-focus-change-next": i18n(
        "DestinationByThemePlacement__ARIA_LABEL_FOCUS_CHANGE_NEXT"
      ),
      "aria-label-focus-change-previous": i18n(
        "DestinationByThemePlacement__ARIA_LABEL_FOCUS_CHANGE_PREVIOUS"
      ),
      "aria-label-next": i18n("DestinationByThemePlacement__ARIA_LABEL_NEXT"),
      "aria-label-page-count": i18n("DestinationByThemePlacement__ARIA_LABEL_PAGE_COUNT"),
      "aria-label-previous": i18n("DestinationByThemePlacement__ARIA_LABEL_PREVIOUS"),
      ...(imageHeight > 0 && { yArrowOffset: imageHeight / 2 - 18 }),
      className: classNames({ [styles.center]: screenSize === XLARGE }),
      items: theme?.stations?.map((item) => renderDestinationByThemeCard(item)),
      onClick: handleListScrollerClick,
      showPositionIndicator: isLargeOrXlarge,
    };
  }

  function handleListScrollerClick() {
    const analyticsValue = placementData?.analyticsValue;

    analyticsValue && fireTrack("squid", { page_description: analyticsValue });
  }

  function getDestinationByThemeCardProps(stationDisplayName, item) {
    return {
      graphic,
      link,
      station: item,
      stationDisplayName,
    };
  }

  function getRecentSearches(searches) {
    return (
      searches
        ?.filter((search) => search?.type === SEARCH_TYPE_AIR)
        .map((search) => search?.formData?.destinationAirportCode) || []
    );
  }

  function getTheme(destinationAirports) {
    const destinationTheme = { stations: [] };
    const stationThemeGroups = getBootstrapData("air/station-theme-groups") || {};
    let themeMatch = false;

    for (const airport of destinationAirports) {
      for (const key in stationThemeGroups) {
        if (!themeMatch && key !== "default" && stationThemeGroups[key].includes(airport)) {
          themeMatch = true;
          destinationTheme.category = key;
          destinationTheme.stations = stationThemeGroups[key];
        }
      }
    }

    if (!themeMatch && "default" in stationThemeGroups) {
      destinationTheme.category = "default";
      destinationTheme.stations = stationThemeGroups["default"];
    }

    if (maxCount && destinationTheme.stations.length > maxCount) {
      destinationTheme.stations.splice(maxCount);
    }

    return destinationTheme;
  }

  function handleImageLoad(value) {
    setImageHeight(value[0].contentRect.height);
  }
};

DestinationByThemePlacement.displayName = "DestinationByThemePlacement";
DestinationByThemePlacement.propTypes = {
  /** Object that contains placement props */
  placement: PropTypes.shape({
    /** Object that contains link/button props. */
    callToAction: PropTypes.object,

    /** Object that contains DestinationByTheme card image props. */
    graphic: PropTypes.shape({
      altText: PropTypes.string,
      imagePath: PropTypes.string,
      override: PropTypes.shape({
        large: PropTypes.shape({
          imagePath: PropTypes.string,
        }),
        medium: PropTypes.shape({
          imagePath: PropTypes.string,
        }),
        small: PropTypes.shape({
          imagePath: PropTypes.string,
        }),
      }),
    }).isRequired,

    /** Object that contains link props. */
    link: PropTypes.shape({
      newWindow: PropTypes.bool,
      override: PropTypes.shape({
        large: PropTypes.shape({
          target: PropTypes.string.isRequired,
        }),
        medium: PropTypes.shape({
          target: PropTypes.string.isRequired,
        }),
        small: PropTypes.shape({
          target: PropTypes.string.isRequired,
        }),
      }),
      target: PropTypes.string,
    }).isRequired,

    /** Limit on number of ListScroller items to display. */
    maxCount: PropTypes.number,
  }),
};
